import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  TestTube,
  FileText,
  DownloadSimple,
  IdentificationCard,
  MagnifyingGlass,
  Target,
  Info,
  CheckCircle,
  Warning,
  Archive,
  Package,
} from "@phosphor-icons/react";
import AdminLayout from "../components/AdminLayout";
import { api, extractError } from "../lib/api";

/**
 * Iter 20.36.f10 — Testsuite (Persona Test Library).
 *
 * Superadmin-only. Zeigt die im Repo definierten fiktiven Test-Personas
 * (unter `/app/backend/tests/personas/`). Für jede Persona kann der Superadmin
 * den PVA-Stil-SV-Auszug und den Lebenslauf als PDF herunterladen und im
 * Einstufungs-Tool durch die Extraction-Pipeline schicken.
 *
 * Zero-Persistence: PDFs werden serverseitig in-memory gerendert.
 */

const SUITE_LABELS = {
  stb: { label: "Steuerberater & WT", color: "bg-amber-50 text-amber-800 border-amber-200" },
  base: { label: "Basis (KV-neutral)", color: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  handel: { label: "Handel", color: "bg-blue-50 text-blue-800 border-blue-200" },
  metall: { label: "Metall/IC/Gewerbe", color: "bg-purple-50 text-purple-800 border-purple-200" },
  it: { label: "IT", color: "bg-cyan-50 text-cyan-800 border-cyan-200" },
  confusion: { label: "Confusion Suite", color: "bg-rose-50 text-rose-800 border-rose-200" },
};

function suiteBadge(suite) {
  const meta = SUITE_LABELS[suite] || {
    label: suite || "sonstige",
    color: "bg-warm-50 text-warm-700 border-warm-200",
  };
  return (
    <span
      className={`inline-flex items-center rounded-sm border px-2 py-0.5 text-[10px] font-medium uppercase tracking-widest ${meta.color}`}
      data-testid="testsuite-persona-suite"
    >
      {meta.label}
    </span>
  );
}

async function downloadBlob(url, filename) {
  const resp = await api.get(url, { responseType: "blob", timeout: 60_000 });
  const blob = new Blob([resp.data], { type: "application/pdf" });
  const cd = resp.headers?.["content-disposition"] || "";
  const m = /filename="([^"]+)"/i.exec(cd);
  const fname = m ? m[1] : filename;
  const link = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = link;
  a.download = fname;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(link), 1000);
  return fname;
}

export default function AdminTestsuitePage() {
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [downloading, setDownloading] = useState(null); // "STB01:sv" | "STB01:cv" | null
  const [query, setQuery] = useState("");

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/testsuite/personas");
      setPersonas(data?.personas || []);
    } catch (e) {
      toast.error(extractError(e, "Testsuite konnte nicht geladen werden."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const openDetail = useCallback(async (persona) => {
    setSelectedId(persona.persona_id);
    setDetail(null);
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/admin/testsuite/personas/${persona.persona_id}`);
      setDetail(data);
    } catch (e) {
      toast.error(extractError(e, "Detail konnte nicht geladen werden."));
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const closeDetail = useCallback(() => {
    setSelectedId(null);
    setDetail(null);
  }, []);

  const doDownload = useCallback(async (persona, kind) => {
    const key = `${persona.persona_id}:${kind}`;
    setDownloading(key);
    try {
      const url = kind === "sv"
        ? `/admin/testsuite/personas/${persona.persona_id}/sv-auszug.pdf`
        : `/admin/testsuite/personas/${persona.persona_id}/lebenslauf.pdf`;
      const fallback = kind === "sv"
        ? `${persona.persona_id}_sv-auszug.pdf`
        : `${persona.persona_id}_lebenslauf.pdf`;
      const fname = await downloadBlob(url, fallback);
      toast.success(`Heruntergeladen: ${fname}`);
    } catch (e) {
      let msg = "Download fehlgeschlagen.";
      try {
        const blob = e?.response?.data;
        if (blob && blob.text) {
          const text = await blob.text();
          const json = JSON.parse(text);
          if (json?.detail) msg = json.detail;
        }
      } catch { /* noop */ }
      toast.error(msg);
    } finally {
      setDownloading(null);
    }
  }, []);

  const doBundleDownload = useCallback(async (suiteSlug) => {
    const key = suiteSlug ? `bundle:${suiteSlug}` : "bundle:all";
    setDownloading(key);
    try {
      const url = suiteSlug
        ? `/admin/testsuite/bundle.zip?suite=${encodeURIComponent(suiteSlug)}`
        : `/admin/testsuite/bundle.zip`;
      const resp = await api.get(url, { responseType: "blob", timeout: 120_000 });
      const blob = new Blob([resp.data], { type: "application/zip" });
      const cd = resp.headers?.["content-disposition"] || "";
      const m = /filename="([^"]+)"/i.exec(cd);
      const fallback = suiteSlug ? `tari-testsuite-${suiteSlug}.zip` : "tari-testsuite.zip";
      const fname = m ? m[1] : fallback;
      const link = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = link;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(link), 1000);
      toast.success(`ZIP heruntergeladen: ${fname}`);
    } catch (e) {
      let msg = "ZIP-Download fehlgeschlagen.";
      try {
        const blob = e?.response?.data;
        if (blob && blob.text) {
          const text = await blob.text();
          const json = JSON.parse(text);
          if (json?.detail) msg = json.detail;
        }
      } catch { /* noop */ }
      toast.error(msg);
    } finally {
      setDownloading(null);
    }
  }, []);

  const filtered = useMemo(() => {
    if (!query.trim()) return personas;
    const q = query.toLowerCase();
    return personas.filter((p) =>
      [p.persona_id, p.persona_name, p.description, p.target_kv, ...(p.testet || [])]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [personas, query]);

  const grouped = useMemo(() => {
    const bySuite = {};
    for (const p of filtered) {
      const s = p.suite || "sonstige";
      if (!bySuite[s]) bySuite[s] = [];
      bySuite[s].push(p);
    }
    return Object.entries(bySuite).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  return (
    <AdminLayout
      title="Testsuite"
      subtitle="Fiktive Test-Personas mit PVA-Stil-SV-Auszügen und Lebensläufen zur manuellen Prüfung der Math-Engine."
      actions={
        <button
          type="button"
          onClick={() => doBundleDownload(null)}
          disabled={downloading === "bundle:all"}
          data-testid="testsuite-download-all-zip"
          className="inline-flex items-center gap-1.5 rounded-sm border border-brand-500 bg-brand-500 px-3 py-1.5 text-sm font-medium text-white transition-crisp hover:bg-brand-600 disabled:opacity-60"
        >
          <Package size={14} weight="bold" />
          {downloading === "bundle:all" ? "ZIP wird gepackt …" : "Alle als ZIP"}
        </button>
      }
    >
      <div className="mb-6 rounded-sm border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="testsuite-intro">
        <div className="flex items-start gap-2">
          <Info size={18} weight="bold" className="mt-0.5 shrink-0 text-amber-700" />
          <div>
            <div className="font-medium">So funktioniert die Testsuite</div>
            <p className="mt-1 leading-relaxed">
              Jede Persona liefert einen SV-Auszug (im Layout eines echten Versicherungsdatenauszuges)
              plus einen Lebenslauf als PDF. PDFs werden auf Klick <strong>in-memory</strong> erzeugt
              und heruntergeladen — <strong>nichts wird gespeichert</strong>. Lade die Dokumente
              anschließend im Einstufungs-Tool hoch (Schritt 3), um die neu implementierten
              KV-Sonderregeln (WT-Kombiregel, Altersfaktor, SV-Status-Max-Dauer)
              gegen Erwartungswerte aus der YAML-Beschreibung zu prüfen.
            </p>
          </div>
        </div>
      </div>

      <div className="mb-4 flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-warm-500" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Suchen: ID, Name, Zweck …"
            data-testid="testsuite-search"
            className="w-full pl-9 pr-3 py-2 text-sm border border-ink-200 rounded-sm bg-white text-ink-900 placeholder:text-warm-500 focus:outline-none focus:border-brand-400"
          />
        </div>
        <div className="text-xs text-warm-600" data-testid="testsuite-count">
          {loading ? "Lädt …" : `${filtered.length} Personas`}
        </div>
      </div>

      {loading && (
        <div className="rounded-sm border border-ink-200 bg-white p-12 text-center text-warm-500" data-testid="testsuite-loading">
          Personas werden geladen …
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="rounded-sm border border-ink-200 bg-white p-12 text-center" data-testid="testsuite-empty">
          <TestTube size={40} weight="thin" className="mx-auto text-warm-400" />
          <div className="mt-3 text-sm text-warm-600">
            Keine Personas gefunden.
          </div>
        </div>
      )}

      {!loading && grouped.map(([suite, list]) => (
        <section key={suite} className="mb-8" data-testid={`testsuite-suite-${suite}`}>
          <div className="mb-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              {suiteBadge(suite)}
              <span className="text-xs text-warm-600">{list.length} {list.length === 1 ? "Persona" : "Personas"}</span>
            </div>
            <button
              type="button"
              onClick={() => doBundleDownload(suite)}
              disabled={downloading === `bundle:${suite}`}
              data-testid={`testsuite-download-suite-zip-${suite}`}
              className="inline-flex items-center gap-1.5 rounded-sm border border-ink-300 bg-white px-2.5 py-1 text-[11px] font-medium text-ink-800 transition-crisp hover:bg-ink-50 hover:border-brand-400 disabled:opacity-60"
              title={`Alle Personas dieser Suite als ZIP herunterladen`}
            >
              <Archive size={12} weight="bold" />
              {downloading === `bundle:${suite}` ? "ZIP …" : "Suite als ZIP"}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {list.map((p) => (
              <div
                key={p.persona_id}
                className="group rounded-sm border border-ink-200 bg-white p-4 transition-crisp hover:border-brand-300 hover:shadow-sm"
                data-testid={`testsuite-persona-card-${p.persona_id}`}
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs bg-ink-900 text-white px-1.5 py-0.5 rounded-sm">
                        {p.persona_id}
                      </span>
                      <h3 className="font-medium text-ink-900 text-sm truncate" data-testid={`testsuite-persona-name-${p.persona_id}`}>
                        {p.persona_name}
                      </h3>
                    </div>
                    <p className="mt-1.5 text-xs text-warm-700 leading-relaxed">
                      {p.description}
                    </p>
                    {p.target_kv && (
                      <div className="mt-1.5 text-[11px] text-warm-500">
                        <Target size={11} weight="bold" className="inline mr-1" />
                        KV: <span className="text-warm-700">{p.target_kv}</span>
                      </div>
                    )}
                  </div>
                </div>

                {p.testet && p.testet.length > 0 && (
                  <ul className="mt-2 mb-3 space-y-0.5">
                    {p.testet.slice(0, 3).map((t, i) => (
                      <li key={i} className="text-[11px] text-warm-600 pl-3 relative">
                        <span className="absolute left-0 text-brand-500">•</span>{t}
                      </li>
                    ))}
                    {p.testet.length > 3 && (
                      <li className="text-[10px] text-warm-500 pl-3">+ {p.testet.length - 3} weitere</li>
                    )}
                  </ul>
                )}

                <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-ink-100">
                  {p.has_sv_auszug && (
                    <button
                      type="button"
                      onClick={() => doDownload(p, "sv")}
                      disabled={downloading === `${p.persona_id}:sv`}
                      data-testid={`testsuite-download-sv-${p.persona_id}`}
                      className="inline-flex items-center gap-1.5 rounded-sm border border-ink-300 bg-white px-2.5 py-1 text-[11px] font-medium text-ink-800 transition-crisp hover:bg-ink-50 hover:border-brand-400 disabled:opacity-50"
                    >
                      <IdentificationCard size={12} weight="bold" />
                      {downloading === `${p.persona_id}:sv` ? "Lädt …" : "SV-Auszug PDF"}
                    </button>
                  )}
                  {p.has_lebenslauf && (
                    <button
                      type="button"
                      onClick={() => doDownload(p, "cv")}
                      disabled={downloading === `${p.persona_id}:cv`}
                      data-testid={`testsuite-download-cv-${p.persona_id}`}
                      className="inline-flex items-center gap-1.5 rounded-sm border border-ink-300 bg-white px-2.5 py-1 text-[11px] font-medium text-ink-800 transition-crisp hover:bg-ink-50 hover:border-brand-400 disabled:opacity-50"
                    >
                      <FileText size={12} weight="bold" />
                      {downloading === `${p.persona_id}:cv` ? "Lädt …" : "Lebenslauf PDF"}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => openDetail(p)}
                    data-testid={`testsuite-open-detail-${p.persona_id}`}
                    className="ml-auto inline-flex items-center gap-1 text-[11px] text-brand-700 hover:text-brand-800 hover:underline"
                  >
                    Details ansehen
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}

      {selectedId && (
        <PersonaDetailModal
          detail={detail}
          loading={detailLoading}
          onClose={closeDetail}
          onDownload={(kind) => detail && doDownload(detail, kind)}
          downloading={downloading}
        />
      )}
    </AdminLayout>
  );
}

function PersonaDetailModal({ detail, loading, onClose, onDownload, downloading }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
      onClick={onClose}
      data-testid="testsuite-detail-modal"
    >
      <div
        className="bg-white rounded-sm shadow-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-ink-200 px-6 py-4 flex items-center justify-between">
          <div>
            {detail && (
              <>
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono text-xs bg-ink-900 text-white px-2 py-0.5 rounded-sm">
                    {detail.persona_id}
                  </span>
                  <h2 className="font-medium text-lg text-ink-900" data-testid="testsuite-detail-name">
                    {detail.persona_name}
                  </h2>
                </div>
                <p className="text-xs text-warm-600">{detail.description}</p>
              </>
            )}
            {!detail && !loading && <div className="text-sm text-warm-500">Kein Detail geladen.</div>}
            {loading && <div className="text-sm text-warm-500">Lädt Detail …</div>}
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="testsuite-detail-close"
            className="text-warm-500 hover:text-ink-900 text-xl"
          >
            ×
          </button>
        </div>

        {detail && (
          <div className="p-6 space-y-6">
            {/* Testet-Liste */}
            {Array.isArray(detail.testet) && detail.testet.length > 0 && (
              <section>
                <h3 className="text-xs uppercase tracking-widest font-semibold text-warm-500 mb-2">
                  Was diese Persona testet
                </h3>
                <ul className="space-y-1">
                  {detail.testet.map((t, i) => (
                    <li key={i} className="text-sm text-ink-800 flex items-start gap-2">
                      <CheckCircle size={14} weight="bold" className="mt-0.5 shrink-0 text-emerald-600" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Target KV */}
            {detail.target_kv && (
              <section>
                <h3 className="text-xs uppercase tracking-widest font-semibold text-warm-500 mb-1">
                  Ziel-KV
                </h3>
                <p className="text-sm text-ink-800">{detail.target_kv}</p>
              </section>
            )}

            {/* SV-Auszug-Zeiten */}
            {detail.sv_auszug?.zeiten && detail.sv_auszug.zeiten.length > 0 && (
              <section data-testid="testsuite-detail-sv-zeiten">
                <h3 className="text-xs uppercase tracking-widest font-semibold text-warm-500 mb-2">
                  SV-Auszug ({detail.sv_auszug.zeiten.length} Einträge)
                </h3>
                <div className="rounded-sm border border-ink-200 overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-warm-50 text-[10px] uppercase tracking-widest text-warm-500">
                      <tr>
                        <th className="text-left px-3 py-2">Nr.</th>
                        <th className="text-left px-3 py-2">von</th>
                        <th className="text-left px-3 py-2">bis</th>
                        <th className="text-left px-3 py-2">Art / Dienstgeber</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.sv_auszug.zeiten.map((grp, i) => (
                        <React.Fragment key={i}>
                          {(grp.zeilen || []).map((z, j) => (
                            <tr key={`${i}-${j}`} className={j === 0 ? "border-t border-ink-100" : ""}>
                              {j === 0 ? (
                                <td rowSpan={(grp.zeilen || []).length} className="px-3 py-1.5 align-top font-mono text-warm-700">
                                  {grp.nr}
                                </td>
                              ) : null}
                              <td className="px-3 py-1.5 font-mono text-ink-800">{z.von}</td>
                              <td className="px-3 py-1.5 font-mono text-ink-800">{z.bis || "laufend"}</td>
                              <td className="px-3 py-1.5">
                                <span className="text-ink-800">{z.art}</span>
                                {j === (grp.zeilen || []).length - 1 && grp.dienstgeber && (
                                  <div className="text-warm-600 mt-0.5">{grp.dienstgeber}</div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {/* Erwartungen */}
            {detail.expected && (
              <section data-testid="testsuite-detail-expected">
                <h3 className="text-xs uppercase tracking-widest font-semibold text-warm-500 mb-2">
                  Erwartetes Ergebnis
                </h3>
                <div className="rounded-sm border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900 space-y-2">
                  {detail.expected.anrechenbare_dauer_ca && (
                    <div>
                      <strong>Anrechenbare Dauer:</strong> {detail.expected.anrechenbare_dauer_ca}
                    </div>
                  )}
                  {Array.isArray(detail.expected.hinweise) && detail.expected.hinweise.length > 0 && (
                    <ul className="space-y-1">
                      {detail.expected.hinweise.map((h, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <Warning size={12} weight="bold" className="mt-1 shrink-0 text-emerald-700" />
                          <span>{h}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </section>
            )}

            {/* Download-Buttons */}
            <section className="border-t border-ink-100 pt-4 flex flex-wrap items-center gap-2">
              {detail.sv_auszug && (
                <button
                  type="button"
                  onClick={() => onDownload("sv")}
                  disabled={downloading === `${detail.persona_id}:sv`}
                  data-testid="testsuite-detail-download-sv"
                  className="inline-flex items-center gap-1.5 rounded-sm border border-brand-500 bg-brand-500 px-3 py-1.5 text-xs font-medium text-white transition-crisp hover:bg-brand-600 disabled:opacity-60"
                >
                  <DownloadSimple size={13} weight="bold" />
                  {downloading === `${detail.persona_id}:sv` ? "Lädt …" : "SV-Auszug herunterladen"}
                </button>
              )}
              {detail.lebenslauf && (
                <button
                  type="button"
                  onClick={() => onDownload("cv")}
                  disabled={downloading === `${detail.persona_id}:cv`}
                  data-testid="testsuite-detail-download-cv"
                  className="inline-flex items-center gap-1.5 rounded-sm border border-ink-300 bg-white px-3 py-1.5 text-xs font-medium text-ink-800 transition-crisp hover:bg-ink-50 disabled:opacity-60"
                >
                  <DownloadSimple size={13} weight="bold" />
                  {downloading === `${detail.persona_id}:cv` ? "Lädt …" : "Lebenslauf herunterladen"}
                </button>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
